import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Repeat, Lock, Users } from 'lucide-react';
import {
  deLocal, diaDelMes, etiquetaHora, fmtHora, hora12, minutosDe, nombreDiaCorto, sumarDias, claveEvento,
} from '../../lib/calendario';
import { disponerBandas, disponerDia, estiloEvento } from './disposicion';

export const HORA_PX = 48;
const PASO = 15;
const BANDA_PX = 22;

const pegar = (m) => Math.round(m / PASO) * PASO;
const pegarAbajo = (m) => Math.floor(m / PASO) * PASO;
const limitar = (v, a, b) => Math.max(a, Math.min(b, v));

/**
 * Vistas Día y Semana: franja de día completo arriba y rejilla de horas debajo.
 *   · clic o arrastre en un hueco → `onCrear({ymd, m0, m1, rect})`
 *   · clic en un evento → `onAbrir(ev, rect)`
 *   · arrastrar un evento → `onMover(ev, {inicio, fin})`; tirar del borde de abajo → cambia la duración
 * Con el dedo solo hay toques (crear y abrir): arrastrar se confunde con desplazar la página.
 */
export default function VistaRejilla({
  dias, tz, hoy, preparados, colorDe, onCrear, onAbrir, onMover, puedeMover, seleccion, onIrDia,
}) {
  const scrollRef = useRef(null);
  const colsRef = useRef(null);
  const arr = useRef(null);
  const [arrastre, setArrastre] = useState(null);
  const [ahora, setAhora] = useState(Date.now());
  const n = dias.length;

  useEffect(() => {
    const t = setInterval(() => setAhora(Date.now()), 60000);
    return () => clearInterval(t);
  }, []);

  // Al abrir: a la hora actual si hoy está a la vista, si no a las 7
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const visibleHoy = dias.includes(hoy);
    const min = visibleHoy ? Math.max(0, minutosDe(Date.now(), tz) - 90) : 7 * 60;
    el.scrollTop = (min / 60) * HORA_PX;
  }, [dias[0], n]); // eslint-disable-line react-hooks/exhaustive-deps

  const { bandas, filas } = useMemo(() => disponerBandas(preparados, dias), [preparados, dias]);
  const porDia = useMemo(() => dias.map((d) => disponerDia(preparados, d)), [preparados, dias]);
  const preparadoDe = useMemo(() => {
    const m = new Map();
    for (const p of preparados) m.set(claveEvento(p.ev), p);
    return m;
  }, [preparados]);

  const punto = (e) => {
    const r = colsRef.current.getBoundingClientRect();
    const col = limitar(Math.floor((e.clientX - r.left) / (r.width / n)), 0, n - 1);
    const min = limitar(((e.clientY - r.top) / HORA_PX) * 60, 0, 1440);
    return { col, min };
  };

  /* ---------------- arrastres ---------------- */
  const empezarCrear = (e) => {
    if (e.button !== 0 || e.target.closest('.cal-ev')) return;
    const { col, min } = punto(e);
    const m0 = pegarAbajo(min);
    arr.current = { tipo: 'crear', col, ancla: m0, m0, m1: Math.min(1440, m0 + 60), x: e.clientX, y: e.clientY, movido: false, touch: e.pointerType === 'touch' };
    if (e.pointerType !== 'touch') {
      try { colsRef.current.setPointerCapture(e.pointerId); } catch (_) { /* sin captura también funciona */ }
    }
    setArrastre({ ...arr.current });
  };

  const empezarEvento = (e, seg, tipo) => {
    if (e.button !== 0) return;
    e.stopPropagation();
    const ev = seg.ev;
    const elemento = e.currentTarget.closest('.cal-ev');
    const movible = puedeMover(ev) && e.pointerType !== 'touch';
    const { col } = punto(e);
    arr.current = {
      tipo: movible ? tipo : 'tocar', seg, ev, col0: col, y0: e.clientY, x0: e.clientX, col, dm: 0, movido: false, elemento,
      m1: seg.m1,
    };
    if (movible) {
      try { colsRef.current.setPointerCapture(e.pointerId); } catch (_) { /* nada */ }
    }
    setArrastre({ ...arr.current });
  };

  const mover = (e) => {
    const a = arr.current;
    if (!a) return;
    const dx = e.clientX - (a.x ?? a.x0);
    const dy = e.clientY - (a.y ?? a.y0);
    if (!a.movido && Math.abs(dx) + Math.abs(dy) < 5) return;
    if (a.touch || a.tipo === 'tocar') return;
    a.movido = true;
    const { col, min } = punto(e);
    if (a.tipo === 'crear') {
      if (col !== a.col) return;
      const m = pegar(min);
      a.m0 = Math.min(a.ancla, m);
      a.m1 = Math.max(a.ancla + PASO, m);
    } else if (a.tipo === 'mover') {
      a.col = col;
      a.dm = pegar((dy / HORA_PX) * 60);
    } else if (a.tipo === 'estirar') {
      a.m1 = limitar(pegar(min), a.seg.m0 + PASO, 1440);
    }
    setArrastre({ ...a });
  };

  const soltar = (e) => {
    const a = arr.current;
    arr.current = null;
    setArrastre(null);
    if (!a) return;
    if (a.tipo === 'crear') {
      const ghost = colsRef.current?.querySelector('.cal-fantasma');
      const rect = ghost ? ghost.getBoundingClientRect() : { left: e.clientX, top: e.clientY, width: 1, height: 1, right: e.clientX, bottom: e.clientY };
      onCrear({ ymd: dias[a.col], m0: a.m0, m1: a.m1, rect });
      return;
    }
    if (!a.movido || a.tipo === 'tocar') {
      onAbrir(a.ev, a.elemento ? a.elemento.getBoundingClientRect() : null);
      return;
    }
    const p = preparadoDe.get(claveEvento(a.ev));
    if (!p) return;
    const durMs = new Date(a.ev.fin) - new Date(a.ev.inicio);
    if (a.tipo === 'mover') {
      const dDias = a.col - a.col0;
      if (!dDias && !a.dm) return;
      let ymd = sumarDias(p.dIni, dDias);
      let min = minutosDe(a.ev.inicio, tz) + a.dm;
      while (min < 0) { min += 1440; ymd = sumarDias(ymd, -1); }
      while (min >= 1440) { min -= 1440; ymd = sumarDias(ymd, 1); }
      const inicio = deLocal(ymd, min, tz);
      onMover(a.ev, { inicio, fin: new Date(inicio.getTime() + durMs) });
    } else if (a.tipo === 'estirar') {
      if (a.m1 === a.seg.m1) return;
      const fin = deLocal(a.seg.ymd, a.m1, tz);
      if (fin <= new Date(a.ev.inicio)) return;
      onMover(a.ev, { inicio: new Date(a.ev.inicio), fin });
    }
  };

  const cancelar = () => { arr.current = null; setArrastre(null); };

  /* ---------------- pintar ---------------- */
  const minAhora = minutosDe(ahora, tz);
  const colHoy = dias.indexOf(hoy);
  const fantasmaCrear = arrastre?.tipo === 'crear' ? arrastre : (seleccion && !seleccion.todoElDia && dias.includes(seleccion.ymd)
    ? { col: dias.indexOf(seleccion.ymd), m0: seleccion.m0, m1: seleccion.m1 } : null);

  return (
    <div className="cal-rejilla" data-testid="cal-rejilla">
      <div className="cal-rejilla-cab" style={{ '--n': n }}>
        <div className="cal-rejilla-esquina">
          <span>{zonaCorta(tz)}</span>
        </div>
        <div className="cal-rejilla-dias">
          {dias.map((d) => (
            <button
              key={d}
              type="button"
              className={`cal-cab-dia ${d === hoy ? 'is-hoy' : ''}`}
              onClick={() => onIrDia && onIrDia(d)}
              aria-label={`Ir al ${d}`}
            >
              <span className="cal-cab-dsem">{nombreDiaCorto(d)}</span>
              <span className="cal-cab-num">{diaDelMes(d)}</span>
            </button>
          ))}
        </div>
        <div className="cal-rejilla-esquina cal-rejilla-esquina--franja"><span>Todo el día</span></div>
        <div
          className="cal-franja"
          style={{ height: Math.max(1, filas) * BANDA_PX + 6 }}
          onClick={(e) => {
            if (e.target.closest('.cal-banda')) return;
            const r = e.currentTarget.getBoundingClientRect();
            const col = limitar(Math.floor((e.clientX - r.left) / (r.width / n)), 0, n - 1);
            onCrear({ ymd: dias[col], todoElDia: true, rect: { left: r.left + (col * r.width) / n, top: r.top, width: r.width / n, height: r.height, right: r.left + ((col + 1) * r.width) / n, bottom: r.bottom } });
          }}
          data-testid="cal-franja"
        >
          {dias.map((d, i) => <span key={d} className="cal-franja-col" style={{ left: `${(i / n) * 100}%`, width: `${100 / n}%` }} />)}
          {bandas.map((b) => (
            <button
              type="button"
              key={`${claveEvento(b.ev)}-${b.col}`}
              className={`cal-banda cal-ev ${b.cortadaIzq ? 'is-cortada-izq' : ''} ${b.cortadaDer ? 'is-cortada-der' : ''}`}
              style={{
                left: `calc(${(b.col / n) * 100}% + 2px)`,
                width: `calc(${(b.span / n) * 100}% - 4px)`,
                top: b.fila * BANDA_PX + 3,
                ...estiloEvento(b.ev, colorDe(b.ev), ahora),
              }}
              onClick={(e) => onAbrir(b.ev, e.currentTarget.getBoundingClientRect())}
              data-testid="cal-evento"
            >
              <span className="truncate">{b.ev.todo_el_dia ? '' : `${fmtHora(b.ev.inicio, tz)} `}{b.ev.titulo || '(Sin título)'}</span>
            </button>
          ))}
        </div>
      </div>

      <div className="cal-rejilla-scroll od-scroll" ref={scrollRef}>
        <div className="cal-rejilla-cuerpo" style={{ height: 24 * HORA_PX }}>
          <div className="cal-horas" aria-hidden="true">
            {Array.from({ length: 24 }, (_, h) => (
              <span key={h} style={{ top: h * HORA_PX }}>{h === 0 ? '' : etiquetaHora(h)}</span>
            ))}
          </div>
          <div
            className="cal-cols"
            ref={colsRef}
            style={{ '--n': n, '--hora': `${HORA_PX}px` }}
            onPointerDown={empezarCrear}
            onPointerMove={mover}
            onPointerUp={soltar}
            onPointerCancel={cancelar}
            data-testid="cal-columnas"
          >
            {dias.map((d, i) => (
              <div key={d} className={`cal-col ${d === hoy ? 'is-hoy' : ''}`} data-ymd={d}>
                {porDia[i].map((s) => {
                  const clave = claveEvento(s.ev);
                  const enArrastre = arrastre && arrastre.ev && claveEvento(arrastre.ev) === clave && arrastre.movido;
                  const alto = Math.max((s.fin - s.m0) / 60 * HORA_PX - 2, 18);
                  const corto = s.m1 - s.m0 <= 35;
                  const ev = s.ev;
                  return (
                    <div
                      key={`${clave}-${s.ymd}`}
                      role="button"
                      tabIndex={0}
                      className={`cal-ev cal-bloque ${corto ? 'is-corto' : ''} ${enArrastre ? 'is-arrastrando' : ''} ${puedeMover(ev) ? 'is-movible' : ''}`}
                      style={{
                        top: (s.m0 / 60) * HORA_PX + 1,
                        height: alto,
                        left: `calc(${(s.col / s.ncols) * 100}% + 1px)`,
                        width: `calc(${(1 / s.ncols) * 100}% - ${s.ncols > 1 ? 3 : 6}px)`,
                        ...estiloEvento(ev, colorDe(ev), ahora),
                      }}
                      onPointerDown={(e) => empezarEvento(e, s, 'mover')}
                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onAbrir(ev, e.currentTarget.getBoundingClientRect()); } }}
                      data-testid="cal-evento"
                      title={`${ev.titulo || '(Sin título)'} · ${fmtHora(ev.inicio, tz)} – ${fmtHora(ev.fin, tz)}`}
                    >
                      <span className="cal-bloque-titulo">
                        {ev.rrule || ev.serie_id ? <Repeat className="cal-bloque-ico" aria-label="Se repite" /> : null}
                        {ev.visibilidad === 'privada' ? <Lock className="cal-bloque-ico" aria-label="Privado" /> : null}
                        {ev.titulo || '(Sin título)'}
                        {corto ? <span className="cal-bloque-hora">, {fmtHora(ev.inicio, tz)}</span> : null}
                      </span>
                      {!corto && (
                        <span className="cal-bloque-hora">
                          {fmtHora(ev.inicio, tz)} – {fmtHora(ev.fin, tz)}
                          {ev.invitados?.length ? <Users className="cal-bloque-ico ml-1" aria-label="Con invitados" /> : null}
                        </span>
                      )}
                      {!corto && ev.ubicacion && s.m1 - s.m0 >= 75 ? <span className="cal-bloque-hora truncate">{ev.ubicacion}</span> : null}
                      {puedeMover(ev) && s.ultimo && (
                        <span className="cal-asa" onPointerDown={(e) => empezarEvento(e, s, 'estirar')} aria-hidden="true" />
                      )}
                    </div>
                  );
                })}
                {/* Evento arrastrado: su silueta en el sitio nuevo */}
                {arrastre?.movido && arrastre.tipo === 'mover' && arrastre.col === i && (() => {
                  const m0 = minutosDe(arrastre.ev.inicio, tz) + arrastre.dm + (arrastre.seg.primero ? 0 : 0);
                  const dur = (new Date(arrastre.ev.fin) - new Date(arrastre.ev.inicio)) / 60000;
                  const top = limitar(m0, 0, 1440);
                  return (
                    <div className="cal-ev cal-bloque cal-sombra" style={{ top: (top / 60) * HORA_PX, height: Math.max(18, (Math.min(dur, 1440 - top) / 60) * HORA_PX - 2), ...estiloEvento(arrastre.ev, colorDe(arrastre.ev), 0) }}>
                      <span className="cal-bloque-titulo">{arrastre.ev.titulo}</span>
                      <span className="cal-bloque-hora">{hora12(m0)} – {hora12(m0 + dur)}</span>
                    </div>
                  );
                })()}
                {arrastre?.movido && arrastre.tipo === 'estirar' && arrastre.seg.ymd === d && (
                  <div className="cal-ev cal-bloque cal-sombra" style={{ top: (arrastre.seg.m0 / 60) * HORA_PX, height: ((arrastre.m1 - arrastre.seg.m0) / 60) * HORA_PX - 2, ...estiloEvento(arrastre.ev, colorDe(arrastre.ev), 0) }}>
                    <span className="cal-bloque-titulo">{arrastre.ev.titulo}</span>
                    <span className="cal-bloque-hora">{hora12(arrastre.seg.m0)} – {hora12(arrastre.m1)}</span>
                  </div>
                )}
                {fantasmaCrear && fantasmaCrear.col === i && (
                  <div className="cal-fantasma" style={{ top: (fantasmaCrear.m0 / 60) * HORA_PX, height: Math.max(12, ((fantasmaCrear.m1 - fantasmaCrear.m0) / 60) * HORA_PX - 2) }}>
                    <span>(Sin título)</span>
                    <span>{hora12(fantasmaCrear.m0)} – {hora12(fantasmaCrear.m1)}</span>
                  </div>
                )}
                {i === colHoy && (
                  <div className="cal-ahora" style={{ top: (minAhora / 60) * HORA_PX }} aria-hidden="true" />
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function zonaCorta(tz) {
  try {
    const t = new Intl.DateTimeFormat('es-MX', { timeZone: tz, timeZoneName: 'shortOffset' }).formatToParts(new Date());
    return (t.find((p) => p.type === 'timeZoneName') || {}).value || '';
  } catch {
    return '';
  }
}
